import { createEffect, createEvent, createStore, attach, combine, Store } from 'effector';
import {
    AliceSkillName,
    AliceSkillNameArray,
    BobSkillName,
    BobSkillNameArray,
    PlayerProfile,
    TutorialStep,
    playerConfigVoid,
    playerProfileStart,
} from '../../../types/alice-bob';
import { FirstResponse, getMe, sync, wipeMe } from '../api/api';
import { $playerConfig } from './playerConfig';
import { AliceUpgradeSpecs, BobUpgradeSpecs } from '../../../types/alice-bob-fe';
import { TUTORIAL_TOKEN, resetTutorialStep, setTutorialStep } from './tutorial';
import { aliceConfig, getUpgradeSpecAlice, getUpgradeSpecBob } from '../../../types/config';
import * as Sentry from "@sentry/browser";

export const $userProfile = createStore<PlayerProfile | null>(null)
export const $firstResponse = createStore<FirstResponse | null>(null)
export const $mePromise = createStore<Promise<FirstResponse> | null>(null);

export const mePromiseSet = createEvent<Promise<FirstResponse> | null>();
export const firstResponseSet = createEvent<FirstResponse | null>();

$userProfile.on(firstResponseSet, (_, profile) => profile?.player);
$firstResponse.on(firstResponseSet, (_, firstResponse) => firstResponse);
$mePromise.on(mePromiseSet, (_, promise) => promise);

export const wipe = createEffect(async () => {
    localStorage.setItem(TUTORIAL_TOKEN, "-1");
    const done = await wipeMe();
    if (done && parseInt(window.Telegram.WebApp.version) >= 8) window.Telegram.WebApp.close()
});

export const getCurrentProgress = (): number => {
    const localStorageProgress = localStorage.getItem('progress');
    if (!localStorageProgress || localStorageProgress == 'null') return 1
    else return parseFloat(localStorageProgress);
}

export const $playerProfile = createStore<PlayerProfile>(playerProfileStart);
export const $afkReward = createStore<{ afk_time: number, afk_reward: number }>({ afk_time: 0, afk_reward: 0 });

export const fetchMeFx = createEffect(async (): Promise<FirstResponse> => {
    // const drop_code = Telegram.getDropCode();
    const userProfile = await getMe();

    window.devtodev.setUserId(userProfile.player.hackney_id);

    if (userProfile.player.tutorial_step == -1) resetTutorialStep() // Tutorial reset from server incoming;
    setTutorialStep(TutorialStep.LOADED_BOOT);

    return userProfile;
});

export const fetchMe = attach({
    source: { mePromise: $mePromise, firstResponse: $firstResponse },
    effect: async ({ mePromise, firstResponse }): Promise<FirstResponse> => {
        if (firstResponse) return firstResponse;

        // Already fetching → reuse in-flight promise
        if (mePromise) return mePromise;

        const promise = fetchMeFx();
        mePromiseSet(promise);

        try {
            const result = await promise;
            firstResponseSet(result);
            mePromiseSet(null);
            return result;
        } catch (err) {
            mePromiseSet(null);
            Sentry.captureException(err)
            throw err;
        }
    },
});

$playerProfile.on(fetchMe.doneData, (_, newProfile) => newProfile.player);
$afkReward.on(fetchMe.doneData, (state, payload) => {
    console.log(payload.afk_reward, payload.afk_time)
    const { afk_time = 0, afk_reward = 0 } = payload;
    return { afk_time, afk_reward }
})
$playerConfig.on(fetchMe.doneData, (_, newData) => newData.game_config || playerConfigVoid);

export const playerAttack = createEvent<number>();
$playerProfile.on(playerAttack, (state, energy) => {
    state.player_wallet.currency_energy -= energy;
    return { ...state };
});

export const syncPlayerGameData = attach({
    source: [$playerProfile],
    effect: async ([playerProfile]) => {
        return await sync(playerProfile);
    },
});
$playerProfile.on(syncPlayerGameData.doneData, (_, newData) => {
    return newData.player;
});

export const tokenEarned = createEvent<number>();
$playerProfile.on(tokenEarned, (state, token) => {
    state.player_wallet.currency_soft += token;
    return { ...state };
});

export const enemyKilled = createEvent();
$playerProfile.on(enemyKilled, (state) => {
    state.score_weekly = (state.score_weekly || 0) + 1;
    return { ...state };
});

export const powerUpAlice = createEvent<Partial<Record<AliceSkillName, number>>>();
$playerProfile.on(powerUpAlice, (state, records) => {
    let totalPrice = 0;
    const skill = Object.keys(records)[0] as AliceSkillName;
    const skillAmount = records[skill] || 1;
    console.log('skill power up' + skill)
    const skillPrice = getUpgradeSpecAlice(skill, (state.hero_alice[skill] || 1) + 1, state.current_chapter).cost

    console.log('skill power up' + skillPrice)
    if (state.player_wallet.currency_soft - skillPrice < 0) return state;
    else state.player_wallet.currency_soft -= skillPrice;

    state.hero_alice[skill] = (state.hero_alice[skill] || 1) + skillAmount;

    window.devtodev.customEvent("lvl_up_alice", {
        "damage_level": state.hero_alice.damage_level,
        "critical_chance_level": state.hero_alice.critical_chance_level,
        "extra_gold_level": state.hero_alice.extra_gold_level,
        'aura_level': state.hero_alice.aura_level
    })
    return { ...state };
});

export const getAliceDamage = (level?: number) => {
    const aliceLvl = level || $playerProfile.getState().hero_alice.damage_level;
    // if (aliceLvl == 50) return { cost: null, attack: null };
    const cost = Math.ceil(
        $playerConfig.getState().base_values.alice_damage_upgrade_base *
        Math.pow($playerConfig.getState().base_values.alice_damage_upgrade_exp, aliceLvl - 1)
    );
    const attack = $playerConfig.getState().base_values.alice_damage_base + aliceLvl * 2;
    return { cost, attack };
};

export const getAliceCritChance = (level?: number) => {
    const aliceLvl = level || $playerProfile.getState().hero_alice.critical_chance_level;
    // if (aliceLvl == 50) return { cost: null, attack: null };
    const cost = Math.ceil(
        $playerConfig.getState().base_values.alice_crit_chance_base_cost *
        Math.pow($playerConfig.getState().base_values.alice_crit_chance_base_cost_multiplication, aliceLvl - 1)
    );
    const value = $playerConfig.getState().base_values.alice_crit_chance_base + aliceLvl * 0.01;
    return { cost, value };
};

export const getAliceExtraGold = (level?: number) => {
    const aliceLvl = level || $playerProfile.getState().hero_alice.extra_gold_level;
    if (aliceLvl == 50) return { cost: null, attack: null };
    const cost = Math.ceil(
        $playerConfig.getState().base_values.alice_extra_gold_base_cost *
        Math.pow($playerConfig.getState().base_values.alice_extra_gold_multiplication, aliceLvl - 1)
    );
    const value =
        aliceLvl <= 25
            ? aliceLvl * $playerConfig.getState().base_values.alice_extra_gold_increment
            : 25 * $playerConfig.getState().base_values.alice_extra_gold_increment +
            ($playerConfig.getState().base_values.alice_extra_gold_increment / 2) * (aliceLvl - 25);
    return { cost, value };
};

export const $aliceUpgradeSpecs: Store<AliceUpgradeSpecs> = combine($playerProfile, (playerProfile): AliceUpgradeSpecs => {
    return AliceSkillNameArray.reduce((acc, skill) => {
        const typedSkill = skill as AliceSkillName;

        const aliceSkillConfig = getUpgradeSpecAlice(typedSkill, (playerProfile.hero_alice[typedSkill] || 1), playerProfile.current_chapter)
        const aliceSkillUpConfig = getUpgradeSpecAlice(typedSkill, (playerProfile.hero_alice[typedSkill] || 1) + 1, playerProfile.current_chapter)

        acc[typedSkill] = {
            value: aliceSkillConfig.value,
            upgradePrice: aliceSkillUpConfig.cost,
            upgradedValue: aliceSkillUpConfig.value,
            canUpgrade: (playerProfile.hero_alice[typedSkill] || 1) < 50,
        }

        return acc;
    }, {} as AliceUpgradeSpecs)
});

export const $bobUpgradeSpecs: Store<BobUpgradeSpecs> = combine($playerProfile, (playerProfile): BobUpgradeSpecs => {
    return AliceSkillNameArray.reduce((acc, skill) => {
        const typedSkill = skill as BobSkillName;

        const aliceSkillConfig = getUpgradeSpecBob(typedSkill, playerProfile.hero_bob[typedSkill], playerProfile.current_chapter)
        const aliceSkillUpConfig = getUpgradeSpecBob(typedSkill, playerProfile.hero_bob[typedSkill] + 1, playerProfile.current_chapter)

        acc[typedSkill] = {
            value: aliceSkillConfig.value,
            upgradePrice: aliceSkillUpConfig.cost,
            upgradedValue: aliceSkillUpConfig.value,
            canUpgrade: playerProfile.hero_bob[typedSkill] < 50,
        }

        return acc;
    }, {} as BobUpgradeSpecs)

    //     let result!: BobUpgradeSpecs;
    //     Object.entries(BobSkillNameArray).forEach(([__, skill], _) => {
    //         const typedSkill = skill as BobSkillName;
    //         const bobConfig = playerConfig.bob[skill as BobSkillName];
    //         result = {
    //             ...result,
    //             [typedSkill]: {
    //                 value: bobConfig[playerProfile.hero_bob[typedSkill]]?.level_value,
    //                 upgradePrice: bobConfig[playerProfile.hero_bob[typedSkill] + 1]?.level_price,
    //                 upgradedValue: bobConfig[playerProfile.hero_bob[typedSkill] + 1]?.level_value || null,
    //                 canUpgrade: bobConfig[playerProfile.hero_bob[typedSkill] + 1]?.level_value ? true : false,
    //             },
    //         };
    //     });
    //     return result;
    // }
});

export const powerUpBob = createEvent<Partial<Record<BobSkillName, number>>>();
$playerProfile.on(powerUpBob, (state, records) => {
    let totalPrice = 0;
    Object.entries(records).forEach(([skill, __], _) => {
        const typedSkill = skill as BobSkillName;
        const nextLevel = $playerProfile.getState().hero_bob[typedSkill];
        // totalPrice += $playerConfig.getState().bob[typedSkill][nextLevel].level_price;
    });
    state.player_wallet.currency_soft -= totalPrice;
    if (state.player_wallet.currency_soft < 0) return state;
    state.hero_bob = sumSkills(state.hero_bob, records);
    return { ...state };
});

function sumSkills<T extends AliceSkillName | BobSkillName>(
    obj1: Record<T, number>,
    obj2: Partial<Record<T, number>>
): Record<T, number> {
    return Object.keys(obj1).reduce((acc, key) => {
        const typedKey = key as T; // Ensure TypeScript understands the key type
        acc[typedKey] = (obj1[typedKey] || 0) + (obj2[typedKey] || 0);
        return acc;
    }, {} as Record<T, number>);
}
